<template>
  <tabs />
  <toolbar />
  <busy-area :busy="loadingBexio || loadingSalesforce">
    <cards class="mb-20">
      <card :title="$t('pages.settings.integrations.bexio')">
        <section class="card-section">
          <integration-status
            :status="bexioStatus"
            :loading="connectingBexio"
            @connect="connect('bexio')"
          />
        </section>
      </card>
      <card :title="$t('pages.settings.integrations.salesforce')">
        <section class="card-section">
          <integration-status
            :status="salesforceStatus"
            :loading="connectingSalesforce"
            @connect="connect('salesforce')"
          />
        </section>
      </card>
    </cards>
  </busy-area>
</template>

<script lang="ts" setup>
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import Toolbar from '@components/utils/toolbar/Toolbar.vue';
import Cards from '@components/utils/Cards.vue';
import Card from '@components/utils/Card.vue';
import Tabs from '@/components/structure/navigation/Tabs.vue';
import BusyArea from '@/components/utils/busy/BusyArea.vue';
import IntegrationStatus from '@/pages/settings/IntegrationStatus.vue';
import { fetchApi } from '@/hooks/fetch';
import { useNotify } from '@/components/utils/notifications';

interface IntegrationStatusResponse {
  status: 'NOT_CONNECTED' | 'CONNECTED' | 'REAUTH_REQUIRED' | 'DISCONNECTED';
  updatedAt?: string;
  connectedBy?: string;
  lastError?: string;
  scope?: string;
  instanceUrl?: string;
}

const { t } = useI18n();
const route = useRoute();
const notify = useNotify();

const bexioStatus = ref<IntegrationStatusResponse | null>(null);
const salesforceStatus = ref<IntegrationStatusResponse | null>(null);
const loadingBexio = ref(false);
const loadingSalesforce = ref(false);
const connectingBexio = ref(false);
const connectingSalesforce = ref(false);

const fetchStatus = async (provider: 'bexio' | 'salesforce') => {
  const loading = provider === 'bexio' ? loadingBexio : loadingSalesforce;
  const statusRef = provider === 'bexio' ? bexioStatus : salesforceStatus;
  try {
    loading.value = true;
    statusRef.value = await fetchApi<IntegrationStatusResponse>(
      `/integrations/${provider}/status`,
    );
  } catch (e) {
    console.error(`Failed to fetch ${provider} status`, e);
    notify?.({
      variant: 'error',
      title: t('pages.settings.integrations.statusError', { provider: provider.charAt(0).toUpperCase() + provider.slice(1) }),
    });
  } finally {
    loading.value = false;
  }
};

const connect = async (provider: 'bexio' | 'salesforce') => {
  const connecting = provider === 'bexio' ? connectingBexio : connectingSalesforce;
  try {
    connecting.value = true;
    const data = await fetchApi<{ authorizeUrl: string }>(
      `/integrations/${provider}/connect`,
    );
    if (data?.authorizeUrl) {
      window.location.href = data.authorizeUrl;
    } else {
      throw new Error('No authorize URL received');
    }
  } catch (e) {
    console.error(`Failed to connect ${provider}`, e);
    notify?.({
      variant: 'error',
      title: t('pages.settings.integrations.connectError', { provider: provider.charAt(0).toUpperCase() + provider.slice(1) }),
    });
  } finally {
    connecting.value = false;
  }
};

const handleConnectedCallback = () => {
  const connected = route.query.connected as string | undefined;
  if (connected && (connected === 'bexio' || connected === 'salesforce')) {
    notify?.({
      variant: 'success',
      title: t('pages.settings.integrations.connectSuccess', { provider: connected.charAt(0).toUpperCase() + connected.slice(1) }),
    });
    // Clean the URL
    const url = new URL(window.location.href);
    url.searchParams.delete('connected');
    window.history.replaceState({}, '', url.toString());
  }
};

onMounted(async () => {
  handleConnectedCallback();
  await Promise.all([fetchStatus('bexio'), fetchStatus('salesforce')]);
});
</script>

<style lang="scss" scoped>
.card-section {
  @apply w-full;
}
</style>
